import { Component, Inject, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash-es';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../workspace.service';
import { AppService } from '../../app.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { environment } from '../../../environments/environment';

/** Import Stateless Rollup — DB staging PROCESS_NAME ≠ screen label. */
const STATELESS_ROLLUP_IMPORT_SCREEN_KEY = '11112';
const STATELESS_ROLLUP_BATCH_PROCESS_NAME = 'CbCR Stateless Rollup - Batch Import';

/** Table 1 Adjustments — UI title is "Import Table 1 Adjs"; DB uses full admin name. */
const TABLE_1_ADJ_IMPORT_SCREEN_KEY = '10880';
const TABLE_1_ADJ_BATCH_PROCESS_NAME = 'Table 1 Adjustments - Batch Import';

@Component({
    selector: 'gtw-batch-process-list-dialog',
    templateUrl: './batch-process-list-dialog.component.html',
    styleUrls: ['./batch-process-list-dialog.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BatchProcessListDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  minimised = false;
  batchList: any[] = [];
  filteredBatchList: any[] = [];
  showBatchLoader = false;
  gridFiltersData = false;
  dropdownBtn = true;
  dropdownSelectionMsg = true;
  showClearBtn = false;
  selectBox = false;
  noDataFoundMsg = false;
  noSearchDataFound = false;
  searchEntity = '';

  taxYears: any[] = [];
  selectedYear: any = {};
  selectedType: any = null;
  types = [
    { name: 'Processed', id: 'Y' },
    { name: 'Unprocessed', id: 'N' },
    { name: 'Removed', id: 'D' },
  ];

  filter: any = { user: '', obj: {}, dateFrom: '', dateTo: '' };

  showDeleteConfirm = false;
  pendingDelete: { transId: string; index: number } | null = null;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Delete Transaction',
    message: 'Are you sure you want to delete this transaction?',
    iconClass: 'fa fa-trash-o',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
  };

  private filteredBatchListCopy: any[] = [];

  constructor(
    private dialogRef: MatDialogRef<BatchProcessListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private http: HttpClient,
    private globalService: GlobalService,
    private workspaceService: WorkspaceService,
    private appService: AppService,
    private gtwSnackbarService: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    const inputs = this.globalService.global?.inputs || {};
    const gp = this.globalService.global?.globalParams || {};

    if (inputs.tax_year?.values) {
      this.taxYears = _.clone(inputs.tax_year.values);
      const found = _.find(this.taxYears, { value: gp.tax_year });
      this.selectedYear = found ? _.cloneDeep(found) : this.taxYears[0] || {};
    }

    const screenKey = String(
      this.workspaceService.workspaceFactory?.activeScreen?.screen_key ??
        this.data?.screenKey ??
        ''
    );
    if (screenKey === STATELESS_ROLLUP_IMPORT_SCREEN_KEY) {
      this.selectedType = this.types.find((type) => type.id === 'Y') ?? null;
      this.dropdownBtn = false;
    }
  }

  typeSelection(): void {
    this.dropdownBtn = false;
  }

  loadProcess(): void {
    if (!this.selectedType) return;
    this.selectBox = true;
    this.dropdownBtn = true;
    this.showClearBtn = true;
    this.showBatchLoader = true;
    this.dropdownSelectionMsg = false;
    this.searchEntity = '';
    this.getBatchTransactions(this.selectedType.id, this.selectedYear.value);
  }

  loadTransactions(index: number, transId: string, enableReprocess: boolean, tag: string): void {
    this.filteredBatchList[index].show_transaction = true;
    const screenKey = this.data.screenKey;
    const commonUrl = environment.batch_api_url;

    this.http
      .get<any>(`${commonUrl}/getBatchProcessesInput`, {
        params: { stage_key: transId, screen_key: screenKey },
      }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          if (res?.callSuccess === '1' && res.jsonObject) {
            const clob = JSON.parse(res.jsonObject.clob_data);
            clob.forEach((row: any) => {
              row.object_id = String(row.object_id);
            });

            const batchData: any = { data: clob };
            batchData.tagNo = tag === 'NA' ? [] : tag.split(',');

            this.filteredBatchList[index].show_transaction = false;
            this.dialogRef.close({ loaded: true, batchData, enableReprocess });
          } else {
            this.filteredBatchList[index].show_transaction = false;
            this.gtwSnackbarService.openSnackBar(
              res?.errorMessage || 'Failed to load transaction.',
              'gtw-snackbar--red'
            );
          }
        },
        () => {
          this.filteredBatchList[index].show_transaction = false;
          this.gtwSnackbarService.openSnackBar(
            'Error loading transaction.',
            'gtw-snackbar--red'
          );
        }
      );
  }

  requestDeleteTran(transId: string, index: number): void {
    this.pendingDelete = { transId, index };
    this.showDeleteConfirm = true;
  }

  onDeleteConfirmed(): void {
    this.showDeleteConfirm = false;
    if (this.pendingDelete) {
      this.deleteTran(this.pendingDelete.transId, this.pendingDelete.index);
      this.pendingDelete = null;
    }
  }

  onDeleteCancelled(): void {
    this.showDeleteConfirm = false;
    this.pendingDelete = null;
  }

  deleteTran(transId: string, index: number): void {
    this.filteredBatchList[index].delete_transaction = true;
    const screenKey = this.data.screenKey;
    const commonUrl = environment.batch_api_url;

    this.http
      .get<any>(`${commonUrl}/deleteBatchProcess`, {
        params: {
          stage_key: transId,
          screen_key: screenKey,
          tax_year: this.selectedYear.value,
        },
      }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.filteredBatchList[index].delete_transaction = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(
              'Transaction deleted successfully.',
              'gtw-snackbar--green'
            );
            this.filteredBatchList.splice(index, 1);
          } else {
            const msg =
              res?.errorMessage === 'no access'
                ? 'Sorry, you do not have access to do the requested action.'
                : res?.errorMessage || 'An error has occurred.';
            this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
          }
        },
        () => {
          this.filteredBatchList[index].delete_transaction = false;
          this.gtwSnackbarService.openSnackBar(
            'Error deleting transaction.',
            'gtw-snackbar--red'
          );
        }
      );
  }

  searchEntityList(): void {
    if (!this.searchEntity?.trim()) return;
    const stageKeys = this.filteredBatchList.map((item: any) => item.TRANSACTION_ID);
    this.filteredBatchList = [];
    this.noSearchDataFound = false;
    this.showBatchLoader = true;

    this.http
      .post<any>(`${environment.base_url}/loadJsonObject?action_code=o6zwbh`, {
        stage_keys: stageKeys.join(','),
        search_text: this.searchEntity,
      }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.showBatchLoader = false;
          if (res?.jsonObject?.length > 0) {
            const matchedIds = res.jsonObject.map((j: any) => j.TRANSACTION_ID);
            this.filteredBatchList = this.filteredBatchListCopy.filter((item: any) =>
              matchedIds.includes(item.TRANSACTION_ID)
            );
          } else {
            this.noSearchDataFound = true;
          }
        },
        () => {
          this.showBatchLoader = false;
          this.noSearchDataFound = true;
        }
      );
  }

  clearEntityList(): void {
    this.filter = { user: '', obj: {}, dateFrom: '', dateTo: '' };
    this.searchEntity = '';
    this.noSearchDataFound = false;
    this.filteredBatchList = [...this.filteredBatchListCopy];
  }

  clearTransView(): void {
    this.selectBox = false;
    this.dropdownBtn = false;
    this.showClearBtn = false;
    this.selectedType = null;
    this.filteredBatchList = [];
    this.dropdownSelectionMsg = true;
    this.noDataFoundMsg = false;
    this.gridFiltersData = false;
    this.noSearchDataFound = false;
    this.filter = { user: '', obj: {}, dateFrom: '', dateTo: '' };
  }

  filterBatchList(): void {
    let filtered = this.batchList.filter((item: any) => {
      if (this.filter.user) {
        const userName =
          (item.USER_FIRST_NAME || '') + ' ' + (item.USER_LAST_NAME || '');
        return userName.toLowerCase().includes(this.filter.user.toLowerCase());
      }
      return true;
    });

    if (this.filter.obj?.TRANSACTION_ID) {
      const tid = this.filter.obj.TRANSACTION_ID;
      filtered = filtered.filter((item: any) =>
        String(item.TRANSACTION_ID).includes(tid)
      );
    }

    if (this.filter.obj?.IMPORT_TAG) {
      const tag = this.filter.obj.IMPORT_TAG.toLowerCase();
      filtered = filtered.filter((item: any) =>
        (item.IMPORT_TAG || '').toLowerCase().includes(tag)
      );
    }

    this.filteredBatchList = filtered;
  }

  close(): void {
    this.dialogRef.close();
  }

  private resolveBatchProcessName(
    screenKey: string | number | null | undefined,
    screenLabel: string
  ): string {
    const key = String(screenKey ?? '');
    if (key === TABLE_1_ADJ_IMPORT_SCREEN_KEY) {
      return TABLE_1_ADJ_BATCH_PROCESS_NAME;
    }
    if (key === STATELESS_ROLLUP_IMPORT_SCREEN_KEY) {
      return STATELESS_ROLLUP_BATCH_PROCESS_NAME;
    }
    return screenLabel || '';
  }

  private getBatchTransactions(statusId: string, year: string): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    const screenKey = activeScreen?.screen_key || this.data.screenKey;
    const processName =
      String(screenKey) === STATELESS_ROLLUP_IMPORT_SCREEN_KEY
        ? STATELESS_ROLLUP_BATCH_PROCESS_NAME
        : (activeScreen?.label || '');
    const commonUrl = environment.batch_api_url;

    this.http
      .get<any>(`${commonUrl}/getBatchProcesses`, {
        params: {
          process_name: processName,
          processed: statusId,
          screen_key: screenKey,
          tax_year: year,
        },
      }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.showBatchLoader = false;
          if (res?.jsonObject?.length > 0) {
            const scenarioList =
              _.find(this.taxYears, { value: year })?.scenarios || [];

            res.jsonObject.forEach((item: any) => {
              item.show_transaction = false;
              item.delete_transaction = false;
              if (item.IMPORT_TAG == null) {
                item.IMPORT_TAG = 'NA';
              }
              const sc = _.find(scenarioList, { scenario: item.SCENARIO });
              item.SCENARIO_DESC = sc?.scenario_desc || item.SCENARIO || '';
            });

            this.gridFiltersData = true;
            this.batchList = res.jsonObject;
            this.filteredBatchList = [...this.batchList];
            this.filteredBatchListCopy = [...this.batchList];
          } else {
            this.noDataFoundMsg = true;
          }
        },
        () => {
          this.showBatchLoader = false;
          this.gtwSnackbarService.openSnackBar(
            'Error loading batch processes.',
            'gtw-snackbar--red'
          );
        }
      );
  }
}
